import { Circle, CircleCheck } from "lucide-react"
import * as React from "react"

import { cn } from "@/lib/utils"

import {
  checkPassword,
  type PasswordRequirement,
  type PasswordStrengthLevel,
} from "./password-rules"

/*
   PasswordStrength — how strong a new password is, and what it still needs.

   A four-segment meter with the level named beside it, and the policy's checklist under it, ticking
   off as the person types. Colour is never the only signal: the level is a word, the segment count
   carries it too, and every requirement has an icon and a screen-reader "(done)" / "(not yet)".

   Colour follows the tokens' meanings: `err` for a password the API would refuse outright, `print`
   for one that is getting there, `master` for one it will accept. The meter can never read green
   while a rule is unmet, because Good and Strong are only said about an acceptable password.
*/

const SEGMENTS = 4

const LEVEL: Record<
  Exclude<PasswordStrengthLevel, "empty">,
  { label: string; filled: number; bar: string; text: string }
> = {
  weak: { label: "Weak", filled: 1, bar: "bg-err", text: "text-err" },
  fair: { label: "Fair", filled: 2, bar: "bg-print", text: "text-print" },
  good: { label: "Good", filled: 3, bar: "bg-master", text: "text-master" },
  strong: { label: "Strong", filled: 4, bar: "bg-master", text: "text-master" },
}

export interface PasswordStrengthProps extends React.ComponentPropsWithoutRef<"div"> {
  password: string
  /**
   * Screen-specific lines appended to the policy's, so a screen with one more condition still has
   * one checklist: "Different from your current password", for instance.
   */
  extraRequirements?: readonly PasswordRequirement[]
}

export const PasswordStrength = React.forwardRef<HTMLDivElement, PasswordStrengthProps>(
  function PasswordStrength({ password, extraRequirements = [], className, ...props }, ref) {
    const { requirements, strength } = checkPassword(password)
    const level = strength === "empty" ? null : LEVEL[strength]
    const lines = [...requirements, ...extraRequirements]

    return (
      <div ref={ref} className={cn("flex flex-col gap-2.5", className)} {...props}>
        <div className="flex items-center gap-2.5">
          <div aria-hidden="true" className="grid flex-1 grid-cols-4 gap-1">
            {Array.from({ length: SEGMENTS }, (_, index) => (
              <span
                key={index}
                className={cn(
                  "h-1 rounded-full transition-colors duration-180 ease-out",
                  level !== null && index < level.filled ? level.bar : "bg-border-strong",
                )}
              />
            ))}
          </div>
          {/* Polite and on the word alone: it changes four times as a password grows, not on
              every keystroke the way the checklist does. */}
          <p
            aria-live="polite"
            className={cn(
              "min-w-12 text-right text-caption font-semibold",
              level ? level.text : "text-text-3",
            )}
          >
            <span className="sr-only">Password strength: </span>
            {level ? level.label : <span className="font-normal">Strength</span>}
          </p>
        </div>

        <ul
          aria-label="Password requirements"
          className="grid gap-x-5 gap-y-1 text-caption sm:grid-cols-2"
        >
          {lines.map((line) => (
            <li
              key={line.id}
              className={cn(
                "flex items-center gap-1.5 transition-colors duration-120 ease-out",
                line.met ? "text-text-2" : "text-text-3",
              )}
            >
              {line.met ? (
                <CircleCheck aria-hidden="true" className="size-3.5 shrink-0 text-master" />
              ) : (
                // text-3, not a border tone: the empty ring is what tells it apart from a tick,
                // so it has to clear 3:1 like any other meaningful graphic.
                <Circle aria-hidden="true" className="size-3.5 shrink-0 text-text-3" />
              )}
              <span>{line.label}</span>
              <span className="sr-only">{line.met ? "(done)" : "(not yet)"}</span>
            </li>
          ))}
        </ul>
      </div>
    )
  },
)
